import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, JSX } from 'react';
import * as R from 'ramda';
import { Row, Col, Form, InputGroup, DropdownButton, Dropdown } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { WtoCondition, WtoRule } from '../../wto-form-context';
import {
  CONDITION_OPTIONS,
  CONDITION_RULE,
  CONDITION_RULE_SINGLE,
  CONDITION_VALUES
} from './rules-form-modal-constants';

interface ConditionsSectionProps {
  newRule: WtoRule;
  setNewRule: Dispatch<SetStateAction<WtoRule>>;
  readOnly: boolean;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  addCondition: () => void;
}

export const ConditionsSection = ({
  newRule,
  setNewRule,
  readOnly,
  onDeleteOperationClick,
  addCondition
}: ConditionsSectionProps) => {
  const editCondition = (
    event: string | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    property: 'trigger' | 'rule' | 'key' | 'value',
    index: number
  ) => {
    const newRuleFormObj = R.clone(newRule);
    const newCondition = newRuleFormObj.conditions[index];
    switch (property) {
      case 'trigger':
        newCondition.trigger = event as string;
        newCondition.rule = '';
        newCondition.value = '';
        break;
      case 'rule':
        newCondition.rule = event as string;
        break;
      case 'key': {
        const valueTokens = newCondition.value.split('=');
        const value = valueTokens.length > 1 ? valueTokens[1] : '';
        newCondition.value = `${(event as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value}=${value}`;
        break;
      }
      case 'value':
        if (typeof event === 'string') {
          newCondition.value = event;
        } else {
          const valueTokens = newCondition.value.split('=');
          newCondition.value = `${valueTokens[0]}=${event.target.value}`;
        }
        break;
      default:
        break;
    }
    if (newCondition.value === '=') {
      newCondition.value = '';
    }
    newRuleFormObj.conditions[index] = newCondition;
    setNewRule(newRuleFormObj);
  };

  const checkListDuplicate = (listName: string) => {
    let result = listName;
    newRule.conditions.map((item: WtoCondition) => {
      if (result === 'Browser') {
        result = item.trigger === 'Browser' ? '' : 'Browser';
      }
      if (result === 'OS') {
        result = item.trigger === 'OS' ? '' : 'OS';
      }
      if (result === 'Device') {
        result = item.trigger === 'Device' ? '' : 'Device';
      }
      return result;
    });
    return result;
  };

  const renderConditionLogic = (condition: WtoCondition, index: number) => {
    const logicSelections: JSX.Element[] = [];
    switch (condition.trigger) {
      case 'UTM Parameter':
      case 'Cookie':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection conditions'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={condition.value ? condition.value.split('=')[0] : ''}
                  disabled={readOnly}
                  data-name={condition.rule}
                  data-type='conditions'
                  placeholder='Key'
                  onChange={e => editCondition(e, 'key', index)}
                />
              </InputGroup>
            </Form.Group>

            <DropdownButton
              className='rule-dropdown'
              disabled={readOnly}
              title={newRule.conditions[index].rule || 'Select Rule'}
            >
              {CONDITION_RULE.map(ruleOption => (
                <Dropdown.Item
                  key={ruleOption}
                  eventKey={ruleOption}
                  onClick={() => editCondition(ruleOption, 'rule', index)}
                >
                  {ruleOption}
                </Dropdown.Item>
              ))}
            </DropdownButton>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={condition.value ? condition.value.split('=')[1] : ''}
                  disabled={readOnly}
                  data-name={condition.rule}
                  data-type='conditions'
                  placeholder='Value'
                  onChange={e => editCondition(e, 'value', index)}
                />
              </InputGroup>
            </Form.Group>
          </div>
        );
        break;
      case 'Device':
      case 'Browser':
      case 'OS':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection conditions'>
            <DropdownButton
              className='rule-dropdown'
              disabled={readOnly}
              title={
                newRule.conditions[index].rule && newRule.conditions[index].rule.length > 0
                  ? newRule.conditions[index].rule
                  : 'Select Rule'
              }
            >
              {CONDITION_RULE_SINGLE.map(ruleOption => (
                <Dropdown.Item
                  key={ruleOption}
                  eventKey={ruleOption}
                  onClick={() => editCondition(ruleOption, 'rule', index)}
                >
                  {ruleOption}
                </Dropdown.Item>
              ))}
            </DropdownButton>
            <DropdownButton
              className='rule-dropdown'
              disabled={readOnly}
              title={
                newRule.conditions[index].value && newRule.conditions[index].value.length > 0
                  ? newRule.conditions[index].value
                  : `Select ${condition.trigger}`
              }
            >
              {CONDITION_VALUES[condition.trigger].map(valueOption => (
                <Dropdown.Item
                  key={valueOption}
                  eventKey={valueOption}
                  onClick={() => editCondition(valueOption, 'value', index)}
                >
                  {valueOption}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
        );
        break;
      default:
        break;
    }
    return logicSelections;
  };

  const renderConditions = () => (
    <div className='conditions-input-wrapper'>
      {newRule.conditions.map((variant: WtoCondition, index: number) => (
        <div key={index} className='condition-fields-wrapper'>
          <div className='operator-wrapper'>
            <div className='conditional-operator'>{variant.operator}</div>
            <DropdownButton
              className='condition-dropdown'
              disabled={readOnly}
              title={
                newRule.conditions[index].trigger && newRule.conditions[index].trigger.length > 0
                  ? newRule.conditions[index].trigger
                  : 'Select Trigger'
              }
            >
              {CONDITION_OPTIONS.map(condition => (
                <Dropdown.Item
                  key={condition}
                  eventKey={condition}
                  disabled={
                    condition !== 'UTM Parameter' &&
                    condition !== 'Cookie' &&
                    condition !== checkListDuplicate('Browser') &&
                    condition !== checkListDuplicate('OS') &&
                    condition !== checkListDuplicate('Device')
                  }
                  onClick={() => editCondition(condition, 'trigger', index)}
                >
                  {condition}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
          {renderConditionLogic(variant, index)}
          {!readOnly && (
            <IconButton
              className='delete-button'
              data-index={index}
              onClick={e => {
                e.preventDefault();
                onDeleteOperationClick(e, 'conditions');
              }}
            >
              <MdDelete />
            </IconButton>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Row>
      <Form.Group as={Col} className='rule-editor'>
        <span className='rule-header'>Conditions</span>
        {renderConditions()}
        <Button
          size={Size.LG}
          disabled={readOnly}
          className='add-rule'
          leadingElement={<HiOutlinePlus />}
          onClick={e => {
            e.preventDefault();
            addCondition();
          }}
        >
          Add Condition
        </Button>
      </Form.Group>
    </Row>
  );
};
